import type { Logger } from "@expert/logging";

import { type ExWoMetricData, sendMetrics } from "./sendMetrics";

export interface WithMetricsOptions {
    logger: Logger;
    rethrow?: boolean;
    logResult?: boolean;
    failureLogLevel?: "error" | "warn";
    /**
     * Called for each caught error. When this returns true, the outcome is treated as an intentional
     * cancel: trace log, `Cancelled` metric instead of `Failure`, and still rethrows if `rethrow` is true.
     */
    shouldCountAsCancelled?: (error: unknown) => boolean;
}

export const withMetricsDimensionName = "actionName";

export async function withMetrics<T>(
    actionName: string,
    action: () => Promise<T>,
    { logger, rethrow = true, logResult = true, failureLogLevel = "warn", shouldCountAsCancelled }: WithMetricsOptions,
) {
    const localLogger = logger.child({ actionName });
    const metricData: ExWoMetricData = { metrics: [], dimensions: { [withMetricsDimensionName]: actionName } };
    try {
        localLogger.debug(`Starting ${actionName}...`);
        metricData.metrics.push({ name: "Start", value: 1 });
        const startExecution = performance.now();
        const result = await action();
        const duration = performance.now() - startExecution;
        localLogger.info(
            { duration, result: logResult && result },
            `${actionName} completed successfully in ${duration}ms.`,
        );
        metricData.metrics.push({ name: "Success", value: 1 });
        metricData.metrics.push({ name: "Duration", value: duration });
        return result;
    } catch (error) {
        const countsAsCancelled = shouldCountAsCancelled?.(error) ?? false;
        if (countsAsCancelled) {
            localLogger.trace({ error }, `${actionName} cancelled`);
            metricData.metrics.push({ name: "Cancelled", value: 1 });
        } else {
            localLogger[failureLogLevel]({ error }, "Action failed");
            metricData.metrics.push({ name: "Failure", value: 1 });
        }
        if (rethrow) throw error;
    } finally {
        void sendMetrics([metricData]);
    }
}
